// ============================================================================
// EmployeeEditModal — edit an employees row. H1 (Antonela 2026-06-02).
//
// Rendered only for hr / superuser (the caller gates on role; RLS is the real
// enforcement — hr_update + superuser_all allow the UPDATE). Demographic and
// employment fields are editable; app_role is intentionally read-only here
// because it drives the auth chain and is managed in Admin, not HR.
// ============================================================================
import { useState, type FormEvent } from 'react';
import { X as XIcon } from 'lucide-react';
import { updateEmployee } from './hooks/use-hr-data';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';

type Rec = Record<string, string | number | null>;

// 1099 joined the employment_type CHECK on 2026-08-25 (TCSD D1). It was missing
// here, which is one reason a 1099 could not be recorded at all — the column
// had no CHECK and this select offered no way to say it.
const EMPLOYMENT_TYPES = ['W2', '1099', 'Subcontractor'];
const EMPLOYMENT_STATUSES = ['Active', 'Former'];

interface Props {
  employee: Rec;
  onClose: () => void;
  onSaved: () => void;
}

function str(v: string | number | null | undefined): string {
  return v === null || v === undefined ? '' : String(v);
}

export default function EmployeeEditModal({ employee, onClose, onSaved }: Props) {
  const e = employee;
  const [first, setFirst] = useState(str(e.employee_name_first));
  const [last, setLast] = useState(str(e.employee_name_last));
  const [title, setTitle] = useState(str(e.employee_title));
  const [empType, setEmpType] = useState(str(e.employment_type) || 'W2');
  const [status, setStatus] = useState(str(e.employment_status) || 'Active');
  const [emailWork, setEmailWork] = useState(str(e.email_work));
  const [emailPersonal, setEmailPersonal] = useState(str(e.email_personal));
  const [phone, setPhone] = useState(str(e.phone));
  const [location, setLocation] = useState(str(e.location));
  const [shift, setShift] = useState(str(e.current_shift));
  const [ecName, setEcName] = useState(str(e.emergency_contact_name));
  const [ecRel, setEcRel] = useState(str(e.emergency_contact_relationship));
  const [ecPhone, setEcPhone] = useState(str(e.emergency_contact_phone));
  const [notes, setNotes] = useState(str(e.notes));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Status options always include the row's current value so an archived/other
  // value still renders rather than silently switching to Active.
  const statusOptions = EMPLOYMENT_STATUSES.includes(status)
    ? EMPLOYMENT_STATUSES
    : [status, ...EMPLOYMENT_STATUSES];

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!first.trim() && !last.trim()) {
      setError('Enter a first or last name.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateEmployee(str(e.employee_id), {
        employee_name_first: first.trim() || null,
        employee_name_last: last.trim() || null,
        employee_title: title.trim() || null,
        employment_type: empType || null,
        employment_status: status || null,
        email_work: emailWork.trim() || null,
        email_personal: emailPersonal.trim() || null,
        phone: phone.trim() || null,
        location: location.trim() || null,
        current_shift: shift.trim() || null,
        emergency_contact_name: ecName.trim() || null,
        emergency_contact_relationship: ecRel.trim() || null,
        emergency_contact_phone: ecPhone.trim() || null,
        notes: notes.trim() || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error('[hr] employee update failed', err);
      setError(errorMessage(err, 'Could not save changes.'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Edit employee ${str(e.employee_id)}`}
      {...overlayClickClose(onClose)}
    >
      <form
        onClick={(ev) => ev.stopPropagation()}
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-tcs-text">Edit employee</h2>
            <p className="truncate text-xs text-tcs-text-muted">
              {str(e.employee_id)} · App role: {str(e.app_role) || '—'} (managed in Admin)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <input value={first} onChange={(ev) => setFirst(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Last name">
              <input value={last} onChange={(ev) => setLast(ev.target.value)} className={inputCls} />
            </Field>
          </div>

          <Field label="Title">
            <input value={title} onChange={(ev) => setTitle(ev.target.value)} className={inputCls} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Employment type">
              <select value={empType} onChange={(ev) => setEmpType(ev.target.value)} className={inputCls}>
                {EMPLOYMENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select value={status} onChange={(ev) => setStatus(ev.target.value)} className={inputCls}>
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Work email">
              <input value={emailWork} onChange={(ev) => setEmailWork(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Personal email">
              <input value={emailPersonal} onChange={(ev) => setEmailPersonal(ev.target.value)} className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <input value={phone} onChange={(ev) => setPhone(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Current shift">
              <input value={shift} onChange={(ev) => setShift(ev.target.value)} className={inputCls} />
            </Field>
          </div>

          <Field label="Location">
            <input value={location} onChange={(ev) => setLocation(ev.target.value)} className={inputCls} />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Emergency contact">
              <input value={ecName} onChange={(ev) => setEcName(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Relationship">
              <input
                value={ecRel}
                onChange={(ev) => setEcRel(ev.target.value)}
                placeholder="Husband, Sister…"
                className={inputCls}
              />
            </Field>
            <Field label="Contact phone">
              <input value={ecPhone} onChange={(ev) => setEcPhone(ev.target.value)} className={inputCls} />
            </Field>
          </div>

          <Field label="Notes">
            <textarea rows={3} value={notes} onChange={(ev) => setNotes(ev.target.value)} className={inputCls} />
          </Field>
        </div>

        {error && (
          <p className="mt-3 rounded-md border border-rag-red bg-st-danger-bg p-2 text-xs text-st-danger-fg-strong">{error}</p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">{label}</span>
      {children}
    </label>
  );
}
